import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { IntlProvider } from 'react-intl'

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, resolveLocale, type SupportedLocale } from './index'
import enMessages from './messages/en.json'

/**
 * Message bundles are imported statically so that Vite bundles the
 * default (English) catalog into the main chunk. When we add another
 * locale, switch this to a dynamic `import()` so only the active
 * bundle is loaded.
 */
const MESSAGES: Record<SupportedLocale, Record<string, string>> = {
  en: enMessages,
}

interface LocaleContextValue {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
  availableLocales: typeof SUPPORTED_LOCALES
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined)

const STORAGE_KEY = 'fleet-portal.locale'

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE
    const stored = window.localStorage.getItem(STORAGE_KEY) as SupportedLocale | null
    if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) return stored
    return resolveLocale()
  })

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const descriptor = SUPPORTED_LOCALES.find((l) => l.code === locale)
      if (descriptor) {
        document.documentElement.lang = descriptor.htmlLang
        document.documentElement.dir = descriptor.direction
      }
    }
  }, [locale])

  const setLocale = useCallback((next: SupportedLocale) => {
    setLocaleState(next)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next)
    }
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, availableLocales: SUPPORTED_LOCALES }),
    [locale, setLocale],
  )

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={MESSAGES[locale] ?? MESSAGES[DEFAULT_LOCALE]}
        // Keep a soft fallback — in dev we surface missing keys loudly,
        // in prod we silently fall back to the ID to avoid a broken UI.
        onError={(err) => {
          if (import.meta.env.DEV) {
            console.warn('[i18n]', err.message)
          }
        }}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  )
}

export function useLocaleController(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    throw new Error('useLocaleController must be used inside <LocaleProvider>')
  }
  return ctx
}
